import dayjs from 'dayjs';

import { Injectable, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import type { DateFilterFn } from '@angular/material/datepicker';

import { DataService } from 'src/app/dashboard/services/data.service';

export interface DateFilterFormType {
  fromDate: FormControl<Date>;
  toDate: FormControl<Date>;
}

@Injectable({ providedIn: 'root' })
export class DateFilterFormBuilder {
  private readonly dataService = inject(DataService);
  fromDateStr: string | null;
  toDateStr: string | null;

  constructor() {
    this.fromDateStr = this.dataService.getFromDate();
    this.toDateStr = this.dataService.getToDate();
  }

  // 日付の初期値を取得して反映
  build = (): DateFilterForm => {
    // クエリパラメータから日付を取得
    const queryParams = new URL(location.href).searchParams;
    const queryFromDate = queryParams.get('date_s');
    const queryToDate = queryParams.get('date_e');

    const parseDate = (dateStr: string | null, fallback: Date): Date => {
      return dateStr ? dayjs(dateStr).toDate() : fallback;
    };

    // 日付のパース
    const finalFromDate = parseDate(
      queryFromDate,
      parseDate(this.fromDateStr, this.oneWeekDaysAgo()),
    );
    const finalToDate = parseDate(
      queryToDate,
      parseDate(this.toDateStr, this.yesterday()),
    );

    // フォームの初期化
    return new DateFilterForm(finalFromDate, finalToDate);
  };

  private fourWeeksDaysAgo = () => dayjs().add(-28, 'day').toDate();
  private oneWeekDaysAgo = () => dayjs().add(-7, 'day').toDate();
  private yesterday = () => dayjs().add(-1, 'day').toDate();
}

export class DateFilterForm {
  private static readonly ONE_YEAR_DAYS = 366;
  private static readonly MIN_DATE = new Date('2019/01/01');
  readonly dateRangeForm: FormGroup;

  // これらは Angular の DI 対象ではないので、inject で読み込む必要はない
  constructor(fromDate?: Date, toDate?: Date) {
    const today = new Date();

    // デフォルト値の設定
    const defaultFromDate =
      fromDate ||
      new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
    const defaultToDate = toDate || today;

    this.dateRangeForm = new FormGroup({
      start: new FormControl(defaultFromDate, Validators.required),
      end: new FormControl(defaultToDate, Validators.required),
    });
  }

  /**
   * 日付の差分を日数で返す
   * @returns 開始日から終了日までの日数
   */
  diffDays(): number {
    const startDate = this.getStartDate();
    const endDate = this.getEndDate();

    if (!startDate || !endDate) {
      return 0;
    }

    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }

  /**
   * フォームが無効かどうかを返す
   * @returns フォームが無効な場合true
   */
  isInvalid(): boolean {
    return this.dateRangeForm.invalid;
  }

  /**
   * 検索期間が無効かどうかを返す
   * @returns 開始日が終了日より後の場合true
   */
  isInvalidSearchPeriod(): boolean {
    const startDate = this.getStartDate();
    const endDate = this.getEndDate();

    if (!startDate || !endDate) {
      return false;
    }

    return startDate > endDate;
  }

  /**
   * 開始日を取得
   * @returns 開始日、ない場合はnull
   */
  getStartDate(): Date | null {
    const startControl = this.dateRangeForm.get('start');
    return startControl?.value || null;
  }

  /**
   * 終了日を取得
   * @returns 終了日、ない場合はnull
   */
  getEndDate(): Date | null {
    const endControl = this.dateRangeForm.get('end');
    return endControl?.value || null;
  }

  /**
   * 開始日のコントロールを取得
   */
  get startDateControl(): FormControl {
    return this.dateRangeForm.get('start') as FormControl;
  }

  /**
   * 終了日のコントロールを取得
   */
  get endDateControl(): FormControl {
    return this.dateRangeForm.get('end') as FormControl;
  }

  // フィルター関数
  fromDateFilter: DateFilterFn<Date | null> = (d: Date | null): boolean => {
    if (!d) {
      return false;
    }
    const endDate = this.getEndDate();
    return DateFilterForm.MIN_DATE <= d && (!endDate || d <= endDate);
  };

  toDateFilter: DateFilterFn<Date | null> = (d: Date | null): boolean => {
    if (!d) {
      return false;
    }
    const startDate = this.getStartDate();
    const today = new Date();
    return (!startDate || startDate <= d) && d <= today;
  };

  // クエリパラメータ取得
  getQueryParams(): { date_s: string; date_e: string } {
    const startDate = this.getStartDate();
    const endDate = this.getEndDate();

    return {
      date_s: startDate ? dayjs(startDate).format('YYYYMMDD') : '',
      date_e: endDate ? dayjs(endDate).format('YYYYMMDD') : '',
    };
  }
}
